import React from 'react';
import { Table, flexRender } from '@tanstack/react-table';
import { ChevronUpIcon, ChevronDownIcon } from 'lucide-react';
import { TableHeader, TableHead, TableRow } from '../../../components/ui/table';
import { StudyData } from '@/types/types';
import { ModalityFilter, InstanceCountFilter } from './tableFilters';
import { TextFilter } from '../../table-common/filters/text-filter';
import {
  DateFilter,
  SingleDateColumnFilter,
} from '../../table-common/filters/date-filter';

interface DataTableHeaderProps {
  table: Table<StudyData>;
}

export const DataTableHeader: React.FC<DataTableHeaderProps> = ({ table }) => {
  return (
    <TableHeader className="sticky top-0 bg-muted z-10">
      {table.getHeaderGroups().map((headerGroup) => (
        <React.Fragment key={headerGroup.id}>
          <TableRow className="!border-b-0 bg-muted">
            {headerGroup.headers.map((header) => {
              const canSort = header.column.getCanSort();
              const sortDirection = header.column.getIsSorted();
              return (
                <TableHead key={header.id} colSpan={header.colSpan}>
                  <div className="flex items-center gap-2 text-foreground">
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                    {canSort && (
                      <span className="ml-2 flex flex-col items-center justify-center w-5">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            header.column.toggleSorting(false);
                          }}
                          className="p-0 m-0 bg-transparent border-none cursor-pointer"
                          tabIndex={0}
                          aria-label="Sort ascending"
                        >
                          <ChevronUpIcon
                            className={`w-4 h-4 transition-opacity duration-200 ${
                              sortDirection === 'asc'
                                ? 'opacity-100 text-blue-600'
                                : 'opacity-40 text-muted-foreground'
                            }`}
                            strokeWidth={2}
                          />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            header.column.toggleSorting(true);
                          }}
                          className="p-0 m-0 bg-transparent border-none cursor-pointer"
                          tabIndex={0}
                          aria-label="Sort descending"
                        >
                          <ChevronDownIcon
                            className={`w-4 h-4 transition-opacity duration-200 ${
                              sortDirection === 'desc'
                                ? 'opacity-100 text-blue-600'
                                : 'opacity-40 text-muted-foreground'
                            }`}
                            strokeWidth={2}
                          />
                        </button>
                      </span>
                    )}
                  </div>
                </TableHead>
              );
            })}
          </TableRow>
          <TableRow className="border-t-0 bg-muted">
            {headerGroup.headers.map((header) => {
              if (
                header.column.id === 'chevron' ||
                header.column.id === 'download' ||
                header.column.id === 'viewer' ||
                header.column.id === 'normalize' ||
                header.column.id === 'slNo'
              ) {
                return <TableHead key={header.id} />;
              }

              return (
                <TableHead key={header.id} className="border-t-0">
                  {header.column.id === 'Modality' ? (
                    <ModalityFilter column={header.column} table={table} />
                  ) : header.column.id === 'StudyDate' ? (
                    <DateFilter column={header.column} />
                  ) : header.column.id === 'UploadDate' ? (
                    <SingleDateColumnFilter
                      column={header.column}
                      placeholder="Pick an upload date"
                    />
                  ) : header.column.id === 'InstanceCount' ? (
                    <InstanceCountFilter column={header.column} />
                  ) : (
                    <TextFilter
                      column={header.column}
                      placeholder={`Enter ${
                        typeof header.column.columnDef.header === 'string'
                          ? header.column.columnDef.header
                          : header.column.id
                      }`}
                    />
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </React.Fragment>
      ))}
    </TableHeader>
  );
};
